<script setup lang="ts">
import axios from 'axios';
import { reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import { API_URL, GOOGLE_MAPS_API_KEY } from '@idem/common/constantes';
import { armarUrl } from '@idem/common/armar-url';

import ModalCropper from './components/modal-cropper.vue';
import { asignarDireccion } from './utils/direccion.utils';

const apiBaseUrl = API_URL || '/api';
const route = useRoute();
const router = useRouter();
const idActividad = route.params.id as string;

let actividadInicial: any = null;
try {
  const res = await axios.get(`${apiBaseUrl}/actividades/${idActividad}`);
  actividadInicial = res.data;
} catch (e: any) {
  if (e.status === 401 || e.response?.status === 401) {
    await router.push('/login');
  }
}

const form = reactive({
  id: idActividad,
  nombre: actividadInicial?.nombre || '',
  descripcion: actividadInicial?.descripcion || '',
  fecha: actividadInicial?.fecha || '',
  hora: actividadInicial?.hora || '',
  direccion: actividadInicial?.direccion || actividadInicial?.direccionActividad || '',
  latitud: actividadInicial?.latitud ?? '',
  longitud: actividadInicial?.longitud ?? '',
  link: actividadInicial?.link || '',
});

const foto = ref<File>();
const fotoCroppeada = ref<File>();
const mostrarCropper = ref(false);
const inputFoto = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string>(actividadInicial?.foto ? armarUrl(actividadInicial.foto) : '');

function onAddressSelected(direccion: any) {
  asignarDireccion(direccion, form);
}

function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const archivo = input.files?.[0];
  if (!archivo) return;
  if (archivo.type !== 'image/jpeg' && archivo.type !== 'image/png') {
    alert('Solo se permiten archivos JPEG y PNG');
    return;
  }
  foto.value = archivo;
  mostrarCropper.value = true;
}

function onConfirm(archivo: File) {
  fotoCroppeada.value = archivo;
  mostrarCropper.value = false;
  if (previewUrl.value && !actividadInicial?.foto) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = URL.createObjectURL(archivo);
}

async function uploadFile(file: File, id: string) {
  const formData = new FormData();
  formData.append('foto', file);
  await axios.post(`${apiBaseUrl}/actividades/${id}/foto`, formData);
}

async function submit() {
  try {
    const payload = {
      id: form.id,
      nombre: form.nombre,
      descripcion: form.descripcion,
      fecha: form.fecha,
      hora: form.hora,
      direccion: form.direccion,
      latitud: form.latitud !== '' && form.latitud !== undefined ? Number(form.latitud) : undefined,
      longitud:
        form.longitud !== '' && form.longitud !== undefined ? Number(form.longitud) : undefined,
      link: form.link,
      estadoPublicacion: 'pendiente',
    };
    await axios.put(`${apiBaseUrl}/actividades`, payload);
    const archivo = fotoCroppeada.value || foto.value;
    if (archivo) {
      await uploadFile(archivo, form.id);
    }
    alert('Actividad guardada correctamente. Ha pasado a estado pendiente.');
    await router.push('/actividades/admin');
  } catch (e: any) {
    alert(e?.response?.data?.message || 'No se pudo guardar la actividad');
  }
}
</script>

<template>
  <section class="mx-auto max-w-2xl px-4 py-8">
    <router-link
      to="/actividades/admin"
      data-testid="boton-volver"
      class="mb-6 inline-block text-teal-600 hover:underline"
    >
      ← Volver a Actividades Publicadas
    </router-link>
    <h1 class="mb-4 text-2xl font-bold text-gray-900">Editar Actividad Publicada</h1>
    <p class="mb-6 text-sm text-amber-700 bg-amber-50 p-3 rounded-md border border-amber-200">
      Nota: Al guardar cambios, la actividad volverá a estado pendiente y requerirá visado.
    </p>

    <form class="space-y-4" @submit.prevent="submit">
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
        <input
          v-model="form.nombre"
          data-testid="input-nombre"
          class="w-full rounded border px-3 py-2"
          required
        />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
        <textarea
          v-model="form.descripcion"
          data-testid="input-descripcion"
          class="w-full rounded border px-3 py-2"
          required
        />
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
          <input
            type="date"
            v-model="form.fecha"
            data-testid="input-fecha"
            class="w-full rounded border px-3 py-2"
            required
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Hora</label>
          <input
            type="time"
            v-model="form.hora"
            data-testid="input-hora"
            class="w-full rounded border px-3 py-2"
            required
          />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
        <google-address-autocomplete
          :apiKey="GOOGLE_MAPS_API_KEY || 'NO_API_KEY_PROVIDED'"
          v-model="form.direccion"
          data-testid="input-direccion"
          class="w-full rounded border px-3 py-2"
          placeholder="Calle 123, Ciudad"
          @keydown.enter="e => e.preventDefault()"
          @callback="onAddressSelected"
        />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Link</label>
        <input
          type="text"
          v-model="form.link"
          data-testid="input-link"
          class="w-full rounded border px-3 py-2"
        />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Foto</label>
        <input
          ref="inputFoto"
          type="file"
          data-testid="input-foto"
          accept="image/jpeg,image/png"
          class="w-full rounded border px-3 py-2"
          @change="handleFileChange"
        />
        <img
          v-if="previewUrl"
          :src="previewUrl"
          alt=""
          data-testid="preview-foto"
          class="mt-2 h-48 w-full cursor-pointer object-contain rounded border"
          @click="inputFoto?.click()"
        />
      </div>
      <modal-cropper v-if="mostrarCropper && foto" :foto="foto" @confirm="onConfirm" />
      <button
        type="submit"
        data-testid="boton-guardar"
        class="rounded bg-teal-600 px-4 py-2 text-white hover:bg-teal-700 font-medium"
      >
        Guardar Cambios
      </button>
    </form>
  </section>
</template>
